import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import Welcome from './pages/Welcome.jsx'
import SignIn from './pages/SignIn.jsx'
import ConnectLinkedIn from './pages/ConnectLinkedIn.jsx'
import Leaderboard from './pages/Leaderboard.jsx'
import LogActivity from './pages/LogActivity.jsx'
import Profile from './pages/Profile.jsx'
import Admin from './pages/Admin.jsx'
import TVMode from './pages/TVMode.jsx'
import Results from './pages/Results.jsx'
import ConnectBanner from './components/ConnectBanner.jsx'
import { useParticipant } from './hooks/useParticipant.js'
import { isMissingSupabaseConfig } from './lib/supabase.js'
import SetupNotice from './components/SetupNotice.jsx'

const SKIP_CONNECT_KEY = 'climatise:skipped-connect'

export default function App() {
  const location = useLocation()
  const { participant, status } = useParticipant()

  if (isMissingSupabaseConfig) {
    return <SetupNotice />
  }

  if (status === 'loading') {
    return <FullPageLoader />
  }

  // Brand-new signed-in user with no LinkedIn yet → push them to /activate/connect.
  // Once they hit "Skip for now" we drop a sessionStorage flag so they don't get
  // bounced back every navigation. The persistent banner keeps reminding them.
  const needsConnect =
    participant &&
    !participant.publora_platform_id &&
    !sessionStorage.getItem(SKIP_CONNECT_KEY) &&
    !['/welcome', '/signin', '/activate/connect', '/admin', '/tv', '/results'].some(
      p => location.pathname.startsWith(p),
    )

  if (needsConnect) {
    return <Navigate to="/activate/connect" replace />
  }

  return (
    <>
      <ConnectBanner participant={participant} />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname.split('/')[1] || 'root'}>
          <Route path="/" element={<RequireAuth><Leaderboard /></RequireAuth>} />
          <Route path="/log" element={<RequireAuth><LogActivity /></RequireAuth>} />
          <Route path="/me" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/admin" element={<RequireAdmin participant={participant}><Admin /></RequireAdmin>} />
          <Route path="/tv" element={<TVMode />} />
          <Route path="/results" element={<Results />} />

          <Route path="/welcome" element={<Welcome />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/activate/connect" element={<RequireAuth><ConnectLinkedIn /></RequireAuth>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </>
  )
}

function RequireAuth({ children }) {
  const { participant, status } = useParticipant()
  if (status === 'loading') return <FullPageLoader />
  if (!participant) return <Navigate to="/welcome" replace />
  return children
}

function RequireAdmin({ participant, children }) {
  if (!participant) return <Navigate to="/welcome" replace />
  if (!participant.is_admin) return <Navigate to="/" replace />
  return children
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg">
      <div className="flex items-center gap-3">
        <div className="w-3 h-3 rounded-full bg-brand-blue animate-pulse" />
        <span className="text-brand-text-muted text-sm">Loading…</span>
      </div>
    </div>
  )
}

export { SKIP_CONNECT_KEY }
